// Entrega de turno estructurada — dominio puro de MiPAE+.
//
// Héroe de la capa de ejercicio. Doble cara por MOMENTO FORMATIVO:
//   - rotación activa (enfermero/a / interno en curso) → entrega real
//     en formato SBAR.
//   - resto (práctica de pregrado, pre internado, sin rol) → "practica tu
//     entrega" (misma estructura, ensayo).
//
// PII por diseño: NUNCA hay campo de texto libre. Todo se arma con selecciones
// cerradas (plantilla por sistema), así que es imposible escribir el nombre, el
// RUT o cualquier dato identificable del paciente. La "cama" es un rótulo
// numérico de ubicación, no un identificador de persona. Nada se persiste.

import { esInternoActivo } from './perfil';

export const MODO = { SBAR: 'sbar', PRACTICA: 'practica' };

// ── Formato del documento ─────────────────────────────────────────────────────
// Eje SEPARADO de MODO, y no hay que confundirlos: MODO dice QUIÉN entrega
// (turno real vs práctica de estudiante), FORMATO dice CON QUÉ ESTRUCTURA se
// imprime exactamente lo mismo. Los datos capturados son idénticos en ambos
// formatos, así que cambiar de formato jamás pierde algo ya marcado: solo se
// re-reparte al imprimir.
//
// IDEAS (Identificación, Diagnóstico, Evolución, Actividades, Soporte) es el
// formato de entrega adoptado por el campo clínico del piloto. SBAR es el que
// enseñan las universidades chilenas y el que MiPAE usó desde el principio, así
// que se conserva seleccionable (no se borra un vocabulario que la enciclopedia
// sigue enseñando).
export const FORMATO = { IDEAS: 'ideas', SBAR: 'sbar' };
export const FORMATO_DEFAULT = FORMATO.IDEAS;
export const FORMATOS = [FORMATO.IDEAS, FORMATO.SBAR];
export const FORMATO_LABEL = { [FORMATO.IDEAS]: 'IDEAS', [FORMATO.SBAR]: 'SBAR' };

// Etiquetas del COMPOSITOR por formato. La captura es la misma en los dos: lo
// que cambia es cómo se llama cada bloque, para que el usuario reconozca en la
// pantalla las letras del documento que va a entregar (coherencia de
// vocabulario: mismo concepto, mismo nombre, en todas las superficies).
export const SECCIONES_UI = {
  [FORMATO.IDEAS]: {
    identificacion: 'Identificación',
    diagnostico: 'Diagnóstico o foco del ingreso',
    evolucion: 'Evolución por sistema',
    actividades: 'Actividades pendientes para el próximo turno',
    letras: 'I, D, E y A',
  },
  [FORMATO.SBAR]: {
    identificacion: 'Situación',
    diagnostico: 'Foco del ingreso',
    evolucion: 'Evaluación por sistema',
    actividades: 'Pendientes para el próximo turno',
    letras: 'S, B, A y R',
  },
};

export function seccionesUi(formato = FORMATO_DEFAULT) {
  return SECCIONES_UI[formato] || SECCIONES_UI[FORMATO_DEFAULT];
}

// Cara de la entrega según el momento formativo del usuario.
//
// El corte NO es "profesional vs estudiante", es "¿entrega turnos de verdad?".
// El interno en rotación activa entrega su turno como cualquiera, así que
// llamarle "práctica" era doblemente falso: el título lo trataba de alumno y,
// peor, la cabecera del texto COPIADO decía "paciente ficticio" sobre una
// entrega real. Se reusa `esInternoActivo` (el mismo criterio que abre la
// Bitácora) para no tener dos definiciones de rotación activa en el repo.
export function modoEntrega(profile) {
  return esInternoActivo(profile) ? MODO.SBAR : MODO.PRACTICA;
}

export const COPY = {
  [MODO.SBAR]: {
    titulo: 'Entrega de turno',
    // La bajada NO nombra el formato: eso lo dice el selector de arriba, que es
    // la fuente única. Si el copy también lo nombrara, cambiar de formato
    // dejaría media pantalla mintiendo.
    bajada:
      'Arma tu entrega en segundos. Plantilla por sistema, sin datos del paciente.',
    cta: 'Generar entrega',
    vacio: 'Marca al menos un hallazgo para armar la entrega.',
  },
  [MODO.PRACTICA]: {
    titulo: 'Practica tu entrega',
    bajada:
      'Ensaya cómo entregar un paciente con la estructura de tu entrega. Sin datos reales: solo la forma.',
    cta: 'Generar mi práctica',
    vacio: 'Marca al menos un hallazgo para practicar tu entrega.',
  },
};

// Nivel global de la situación (S de SBAR).
export const NIVELES = ['Estable', 'En observación', 'Crítico'];

// Aislamiento (S de SBAR) — catálogo cerrado, lo PRIMERO que se dice en una
// entrega real (antes de acercarse a la cama, quién necesita EPP). Espejo de
// registroTurno.AISLAMIENTOS sin 'Ninguno' (no se importa desde ahí: ese
// módulo ya importa DESDE este archivo, así que importar en el otro sentido
// crearía un ciclo).
export const AISLAMIENTOS = ['Preventivo', 'Contacto', 'Gotitas', 'Aéreo'];

// Prosa de una lista de aislamientos ("contacto", "contacto y gotitas",
// "contacto, gotitas y aéreo"). Vive AQUÍ y no en registroTurno.js por la misma
// razón que el catálogo de arriba: este módulo es el de abajo en el grafo de
// imports, así que registroTurno.js la re-exporta y las dos superficies dicen la
// MISMA frase (regla de máxima coherencia: un vocabulario, una función).
// Catálogo cerrado: lo que no está en AISLAMIENTOS no se imprime, y la lista
// vacía devuelve '' (nunca "ninguno", que no es una frase que nadie diga).
export function textoAislamientos(lista = []) {
  const partes = [];
  for (const a of (Array.isArray(lista) ? lista : [])) {
    if (AISLAMIENTOS.includes(a) && !partes.includes(a)) partes.push(a);
  }
  const nombres = partes.map((a) => a.toLowerCase());
  if (!nombres.length) return '';
  if (nombres.length === 1) return nombres[0];
  return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
}

// Foco del ingreso (B de SBAR) — categorías amplias, cerradas, sin PII.
export const FOCOS = ['Médico', 'Quirúrgico', 'Trauma', 'Obstétrico', 'Otro'];

// Plantilla por sistema (A de SBAR). Solo opciones cerradas.
export const SISTEMAS = [
  {
    id: 'neuro',
    label: 'Neurológico',
    opciones: [
      'Vigil y orientado', 'Somnoliento', 'Sopor', 'Agitación',
      'Dolor controlado', 'Dolor no controlado', 'Déficit nuevo',
    ],
  },
  {
    id: 'respiratorio',
    label: 'Respiratorio',
    opciones: [
      'Aire ambiente', 'Naricera', 'Mascarilla', 'VMNI', 'VMI',
      'Saturación en rango', 'Disnea', 'Secreciones abundantes',
    ],
  },
  {
    id: 'cardiovascular',
    label: 'Cardiovascular',
    opciones: [
      'Hemodinamia estable', 'Hipotensión', 'Hipertensión',
      'Taquicardia', 'Bradicardia', 'Arritmia', 'Drogas vasoactivas',
    ],
  },
  {
    id: 'digestivo',
    label: 'Digestivo / Eliminación',
    opciones: [
      'Tolera vía oral', 'Régimen cero', 'SNG', 'Náuseas o vómitos',
      'Diuresis conservada', 'Oliguria', 'Sin tránsito intestinal',
    ],
  },
  {
    id: 'tegumentario',
    label: 'Piel y accesos',
    opciones: [
      'Piel indemne', 'Lesión por presión', 'Herida operatoria',
      'Vía venosa periférica', 'Catéter venoso central', 'Sonda Foley', 'Drenajes',
    ],
  },
];

// Opciones que describen algo que el paciente LLEVA (un soporte o dispositivo),
// no un hallazgo de su evolución. Es la S de IDEAS, que separa lo que el
// paciente ES de lo que le está sosteniendo: la vía aérea y el oxígeno, las
// drogas vasoactivas, la sonda de alimentación y los accesos.
//
// No es un catálogo nuevo: son literalmente las mismas opciones cerradas de
// SISTEMAS, acá solo se declara a qué sección van cuando el formato separa
// evolución de soporte. En SBAR no se usa (la A las dice junto al resto, como
// siempre). Fijado por test contra SISTEMAS para que un rename de opción no
// deje un soporte huérfano imprimiéndose como hallazgo.
export const OPCIONES_SOPORTE = new Set([
  // Respiratorio: la vía aérea y el oxígeno son EL soporte de la cama.
  'Aire ambiente', 'Naricera', 'Mascarilla', 'VMNI', 'VMI',
  // Cardiovascular.
  'Drogas vasoactivas',
  // Digestivo: la sonda es dispositivo; el régimen es evolución, no soporte.
  'SNG',
  // Accesos y drenajes.
  'Vía venosa periférica', 'Catéter venoso central', 'Sonda Foley', 'Drenajes',
]);

// Pendientes / recomendación (R de SBAR, A de IDEAS).
export const PENDIENTES = [
  'Exámenes', 'Imágenes', 'Interconsulta', 'Curación',
  'Traslado', 'Ajuste de fármacos', 'Reevaluar dolor', 'Nada pendiente',
];

// Estado inicial vacío del compositor.
export function estadoInicial() {
  return {
    cama: '',
    nivel: null,
    // `aislamiento` sigue siendo el MÁS severo (compatibilidad con todo lo que
    // ordena o cuenta con un solo valor) y `aislamientos` la lista completa: un
    // paciente puede estar en contacto Y gotitas a la vez (2026-09-10).
    aislamiento: null,
    aislamientos: [],
    foco: null,
    sistemas: {},   // { sistemaId: [opcion, ...] }
    pendientes: [], // [opcion, ...]
  };
}

// Solo dígitos: la cama es ubicación, nunca un identificador de persona.
export function sanitizarCama(valor) {
  return String(valor ?? '').replace(/\D/g, '').slice(0, 4);
}

// ¿Hay al menos un hallazgo clínico marcado? (la cama/nivel sola no basta).
export function tieneContenido(state) {
  const algunSistema = Object.values(state?.sistemas || {}).some(
    (arr) => Array.isArray(arr) && arr.length > 0,
  );
  return algunSistema || (state?.pendientes?.length > 0);
}

// ── Revisión de la entrega (red de seguridad determinística) ──────────────────
// "Revisa tu entrega antes de darla": como la entrega es estructurada, podemos
// detectar huecos y contradicciones sin IA — instantáneo y sin costo. No corrige
// el contenido clínico (eso es del enfermero); avisa de lo que falta o no calza
// en la estructura SBAR. Puro y testeable.
//
// Devuelve { ok, avisos: [{ id, campo, nivel, mensaje }], completos: {S,B,A,R} }.
// nivel: 'alta' (hueco que rompe la entrega) | 'media' (conviene revisar).
export function revisarEntrega(state) {
  const s = state || estadoInicial();
  const avisos = [];
  const add = (id, campo, nivel, mensaje) => avisos.push({ id, campo, nivel, mensaje });

  const opcionesMarcadas = Object.values(s.sistemas || {})
    .flatMap((arr) => (Array.isArray(arr) ? arr : []));
  const tiene = (op) => opcionesMarcadas.includes(op);
  const pend = Array.isArray(s.pendientes) ? s.pendientes : [];

  // Los `campo` siguen siendo las letras del SBAR porque son la CLAVE interna
  // del chequeo (estable para tests y telemetría); la letra que se MUESTRA la
  // traduce `letraCampo` según el formato elegido. Por eso ningún mensaje
  // nombra su letra: en IDEAS diría la equivocada.
  // S — Situación / Identificación
  if (!s.nivel) add('sin-nivel', 'S', 'alta', 'Falta el estado general: ¿estable, en observación o crítico?');
  // B — Antecedentes / Diagnóstico
  if (!s.foco) add('sin-foco', 'B', 'media', 'Falta el foco del ingreso: médico, quirúrgico, trauma…');
  // A — Evaluación / Evolución
  if (opcionesMarcadas.length === 0) add('sin-sistemas', 'A', 'alta', 'No evaluaste ningún sistema: marca al menos los hallazgos relevantes.');
  // R — Recomendación / Actividades
  if (pend.length === 0) add('sin-pendientes', 'R', 'media', "No indicaste pendientes. Si no hay, marca 'Nada pendiente' para dejarlo explícito.");
  if (pend.includes('Nada pendiente') && pend.length > 1) add('pendientes-contradictorios', 'R', 'media', "Marcaste 'Nada pendiente' junto a otros pendientes: deja solo lo que corresponde.");

  // Coherencia clínica (alto valor, determinística)
  const gravesConEstable = ['Drogas vasoactivas', 'VMI'].filter(tiene);
  if (s.nivel === 'Estable' && gravesConEstable.length) {
    add('gravedad-incoherente', 'A', 'media', `Marcaste ${gravesConEstable.join(' y ')} pero el estado es 'Estable': revisa la clasificación.`);
  }
  if (tiene('Dolor no controlado') && !pend.includes('Reevaluar dolor')) {
    add('dolor-sin-plan', 'R', 'media', "Dolor no controlado sin 'Reevaluar dolor' en pendientes: agrégalo al plan del próximo turno.");
  }

  return {
    avisos,
    ok: avisos.length === 0,
    completos: {
      S: !!s.nivel,
      B: !!s.foco,
      A: opcionesMarcadas.length > 0,
      R: pend.length > 0,
    },
  };
}

// Pendientes del próximo turno: catálogo cerrado (strings, de siempre) + tareas
// con hora del turno (objetos { tipo, hora? } que turnoBridge.entregaDesdeRegistro
// vierte desde registro.tareas — fix #2 de la auditoría). Conviven sin romper
// los chips existentes: no se acusa, se ENTREGA, "queda para el turno entrante".
//
// Fuente ÚNICA de esta línea: es la R del SBAR y la A de IDEAS, y tiene que
// decir exactamente lo mismo en los dos (no duplicar la compilación).
function textoPendientes(s) {
  const pendCatalogo = PENDIENTES.filter((p) => s.pendientes?.includes(p));
  const pendTareas = (Array.isArray(s.pendientes) ? s.pendientes : [])
    .filter((p) => p && typeof p === 'object' && p.tipo)
    .map((p) => (p.hora ? `${p.tipo} a las ${p.hora}` : p.tipo));
  const pend = [...pendCatalogo, ...pendTareas];
  return pend.length ? pend.join(', ') + '.' : 'Sin pendientes para el próximo turno.';
}

// Cabecera del documento. La práctica manda sobre el formato: lo primero que
// tiene que saber quien lee es que el paciente es ficticio.
function cabeceraDe(profile, formato) {
  return modoEntrega(profile) === MODO.PRACTICA
    ? 'PRÁCTICA DE ENTREGA (paciente ficticio)'
    : `ENTREGA DE TURNO (${FORMATO_LABEL[formato] || FORMATO_LABEL[FORMATO_DEFAULT]})`;
}

// La línea de aislamiento de la S (y de la I de IDEAS), UNA sola vez: lee la
// lista completa y, si el compositor todavía trae solo el valor único (estado
// viejo en sessionStorage, o una entrega armada a mano), cae al `aislamiento`
// de siempre. Sin lista y sin valor válido devuelve null: no se afirma nada.
function fraseAislamiento(s) {
  const lista = Array.isArray(s?.aislamientos) && s.aislamientos.length
    ? s.aislamientos
    : [s?.aislamiento];
  const texto = textoAislamientos(lista);
  return texto ? `Aislamiento ${texto}` : null;
}

// Construye las cuatro secciones SBAR a partir del estado cerrado.
// Devuelve { S, B, A, R, texto }. Nunca incluye texto libre SIN filtrar: la
// única excepción es el `detalle` de una tarea con hora (R), que ya pasó por
// la guarda anti-PII al crearse (ver registroTurno.nuevaTarea/notaConPii).
export function buildEntrega(state, profile) {
  const s = state || estadoInicial();
  const cama = sanitizarCama(s.cama);

  // Aislamiento primero: en una entrega real es lo PRIMERO que se dice (define
  // si hay que ponerse EPP antes de acercarse a la cama).
  const S = [
    fraseAislamiento(s),
    cama ? `Cama ${cama}` : 'Paciente',
    s.nivel || 'Sin clasificar',
  ].filter(Boolean).join(' · ');

  const B = s.foco ? `Ingreso ${s.foco.toLowerCase()}.` : 'Antecedentes según ficha.';

  const lineasA = SISTEMAS
    .map((sis) => {
      const sel = s.sistemas?.[sis.id] || [];
      if (!sel.length) return null;
      // Respeta el orden canónico de opciones del sistema.
      const orden = sis.opciones.filter((o) => sel.includes(o));
      return `• ${sis.label}: ${orden.join(', ')}.`;
    })
    .filter(Boolean);
  const A = lineasA.length ? lineasA.join('\n') : '• Sin hallazgos marcados.';

  const R = textoPendientes(s);

  const cabecera = cabeceraDe(profile, FORMATO.SBAR);

  // Sin guiones largos en el texto que se copia y se pega (norma del
  // 2026-07-16): las letras del SBAR llevan su palabra entre paréntesis y los
  // pendientes dicen "a las HH:MM", igual que ya los reporta Benito en
  // `mis_pendientes`. No reintroducir el guion largo por "verse más limpio".
  const texto = [
    cabecera,
    '',
    `S (Situación): ${S}.`,
    `B (Antecedentes): ${B}`,
    'A (Evaluación):',
    A,
    `R (Recomendación): ${R}`,
  ].join('\n');

  return { S, B, A, R, texto };
}

// Construye las cinco secciones IDEAS a partir del MISMO estado cerrado que el
// SBAR: Identificación, Diagnóstico, Evolución, Actividades, Soporte.
// Devuelve { I, D, E, A, S, texto }.
//
// No se captura ni un dato nuevo: lo que cambia es el reparto al imprimir. La
// diferencia real con el SBAR es que IDEAS separa lo que el paciente ES (su
// evolución) de lo que LLEVA (soportes y dispositivos), que en el SBAR van
// mezclados en la A. Ese reparto lo decide `OPCIONES_SOPORTE`.
//
// Misma promesa PII que el SBAR: todo sale de catálogos cerrados y la única
// excepción es el `detalle` de una tarea con hora, que ya pasó la guarda
// anti-PII al crearse (registroTurno.nuevaTarea/notaConPii).
export function buildEntregaIdeas(state, profile) {
  const s = state || estadoInicial();
  const cama = sanitizarCama(s.cama);

  // Identificación: mismo arranque que la S del SBAR, y por la misma razón
  // clínica — el aislamiento va PRIMERO porque define si hay que ponerse EPP
  // antes de acercarse a la cama. La cama es ubicación, nunca una persona.
  const I = [
    fraseAislamiento(s),
    cama ? `Cama ${cama}` : 'Paciente',
    s.nivel || 'Sin clasificar',
  ].filter(Boolean).join(' · ');

  const D = s.foco ? `Ingreso ${s.foco.toLowerCase()}.` : 'Diagnóstico según ficha.';

  // Un solo recorrido reparte lo marcado en dos secciones, respetando el orden
  // canónico de opciones dentro de cada sistema y el orden de SISTEMAS entre
  // ellos (el mismo del SBAR: quien conoce un documento reconoce el otro).
  const lineasE = [];
  const soportes = [];
  for (const sis of SISTEMAS) {
    const sel = s.sistemas?.[sis.id] || [];
    if (!sel.length) continue;
    const orden = sis.opciones.filter((o) => sel.includes(o));
    const hallazgos = orden.filter((o) => !OPCIONES_SOPORTE.has(o));
    for (const o of orden) if (OPCIONES_SOPORTE.has(o)) soportes.push(o);
    if (hallazgos.length) lineasE.push(`• ${sis.label}: ${hallazgos.join(', ')}.`);
  }
  const E = lineasE.length ? lineasE.join('\n') : '• Sin hallazgos marcados.';

  // "Sin dispositivos ni soportes MARCADOS": dice lo que se marcó, no afirma
  // que el paciente no lleve nada (misma disciplina que "Sin hallazgos
  // marcados" y que las basales del examen: no se declara lo no constatado).
  const S = soportes.length ? soportes.join(', ') + '.' : 'Sin dispositivos ni soportes marcados.';

  const A = textoPendientes(s);

  // Mismo criterio del SBAR: sin guiones largos en lo que se copia y se lee en
  // voz alta, y cada letra con su palabra entre paréntesis. El orden de las
  // secciones es el del modelo (I, D, E, A, S) y no se reordena "porque queda
  // mejor": quien recibe el turno lee siempre en el mismo orden.
  const texto = [
    cabeceraDe(profile, FORMATO.IDEAS),
    '',
    `I (Identificación): ${I}.`,
    `D (Diagnóstico): ${D}`,
    'E (Evolución):',
    E,
    `A (Actividades): ${A}`,
    `S (Soporte): ${S}`,
  ].join('\n');

  return { I, D, E, A, S, texto };
}

// Entrada única para la UI: arma el documento en el formato pedido. Ambas
// funciones devuelven `texto`, que es lo único que la pantalla necesita.
export function buildEntregaEnFormato(state, profile, formato = FORMATO_DEFAULT) {
  return formato === FORMATO.SBAR
    ? buildEntrega(state, profile)
    : buildEntregaIdeas(state, profile);
}

// La revisión de huecos NO cambia con el formato: falta el estado, falta el
// foco, no evaluaste nada, no dijiste pendientes. Lo único que cambia es la
// LETRA con que se rotula cada hueco, porque el mismo bloque se llama distinto
// en cada documento. Por eso los avisos no nombran su letra en el mensaje: la
// pone la UI con esta función (si no, el aviso mentiría en un formato u otro).
const LETRA_POR_FORMATO = {
  [FORMATO.SBAR]:  { S: 'S', B: 'B', A: 'A', R: 'R' },
  [FORMATO.IDEAS]: { S: 'I', B: 'D', A: 'E', R: 'A' },
};

export function letraCampo(campo, formato = FORMATO_DEFAULT) {
  const mapa = LETRA_POR_FORMATO[formato] || LETRA_POR_FORMATO[FORMATO_DEFAULT];
  return mapa[campo] || campo;
}
